import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { getSvgDefs } from '@ardoq/icons';
import {
  MAIN_GROUP_CONTAINER_SELECTOR,
  NO_EXPORT_SELECTOR,
} from '@ardoq/global-consts';
import { ViewIds } from '@ardoq/api-types';
import * as profiling from '@ardoq/profiling';
import { isSafari } from '@ardoq/common-helpers';
import {
  GraphVisualizationSvgDefElements,
  intersect,
  intersects,
} from '@ardoq/graph';
import { svgCopiedStyles as COPIED_STYLES } from './svgCopiedStyles';
import { getStyles, serializeAndWrapInSVG } from './htmlExport';
import { getCanvasWithBackgroundColor } from './canvasUtil';
import {
  cloneImageColorFilters,
  createExportCanvas,
  getClonedDOM,
} from './exportUtil';
import {
  CANVAS_MAXIMUM_AREA,
  CANVAS_MAXIMUM_DIMENSION,
  DEVICE_PIXEL_RATIO,
  HTML_EXPORT_LEGEND_PADDING,
} from './consts';
import { EXPORT_FORMAT, ExportTrackingData, GetStyleSheetsFn } from './types';
import { getCanvasOfDOM } from './getCanvasOfDOM';
import { ensureImageDataUri } from './ensureImageDataUri';
import { toExportTooLargeError } from './exportSink';

const SVG_IMPORTS =
  'xmlns:svg="http://www.w3.org/2000/svg" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.0"';
const PADDING = 200;

/** encodingUtils.escapeHTML replaces the / character, and we can't have that because of attributes like xmlns:svg="http://www.w3.org/2000/svg" */
const escapeHTMLAttribute = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const setElementStyle = (node: SVGElement) => {
  const computedStyle = window.getComputedStyle(node);
  COPIED_STYLES.forEach(key => {
    node.style.setProperty(
      key,
      computedStyle.getPropertyValue(key),
      computedStyle.getPropertyPriority(key)
    );
  });
};

type FormatSVGForExportArguments = {
  svgText: string;
  width: number;
  height: number;
  legendBBox: DOMRect | undefined;
};
const formatSVGForExport = ({
  svgText = '',
  width,
  height,
  legendBBox,
}: FormatSVGForExportArguments) => {
  // Remove height width and viewBox defs to avoid duplicates (invalid SVG)
  const { height: legendHeight, width: legendWidth } = legendBBox || {
    width: 0,
    height: 0,
  };

  const outerWidth = width + legendWidth + PADDING * 1.5;
  const outerHeight = (height > legendHeight ? height : legendHeight) + PADDING;

  return svgText
    .replace(/^<svg([^>]*?)height=".*?"([^>]*?)>/, '<svg$1$2>')
    .replace(/^<svg([^>]*?)width=".*?"([^>]*?)>/, '<svg$1$2>')
    .replace(/^<svg([^>]*?)viewBox=".*?"([^>]*?)>/, '<svg$1$2>')
    .replace(
      /^<svg /,
      `<svg viewBox="0 0 ${outerWidth} ${outerHeight}" width="${outerWidth}" height="${outerHeight}" `
    );
};

/**
 * Measures the Network View's main group, resizes the SVG to fit the whole thing plus some padding, and centers the main group inside the SVG bounds.
 * @param svg The cloned root SVG from the Network View.
 */
const scaleAndFitNetworkView = (svg: SVGElement) => {
  const mainGroupContainer = svg.querySelector<SVGGElement>(
    MAIN_GROUP_CONTAINER_SELECTOR
  );
  if (!mainGroupContainer) {
    return;
  }
  const { x, y, height, width } = mainGroupContainer.getBBox();
  const translate = [-x + PADDING / 2, -y + PADDING / 2];
  mainGroupContainer.setAttribute('transform', `translate(${translate})`);
  svg.style.setProperty('width', `${width + PADDING}px`);
  svg.style.setProperty('height', `${height + PADDING}px`);
};

/**
 * The id of the clipPath element must correspond to the clipPath url used on the mainContainer
 * @returns clientBoundingClientRect of the clipPath element
 */
const getVisibleClippedAreaBounds = (
  svg: SVGElement,
  mainGroupContainer: Element
) => {
  const clipPathRect = getClipPathElementByUrl(
    svg,
    mainGroupContainer.getAttribute('clip-path')
  )?.getBoundingClientRect();
  const mainGroupRect = mainGroupContainer.getBoundingClientRect();

  return clipPathRect && mainGroupRect
    ? intersect(clipPathRect, mainGroupRect)
    : mainGroupRect;
};

/**
 * @returns the clipPath element with the id corresponding to the clip-path url
 */
const getClipPathElementByUrl = (
  svg: SVGElement,
  clipPathUrl: string | null
) =>
  clipPathUrl
    ? svg.querySelector<SVGElement>(
        `#${clipPathUrl.substring(
          clipPathUrl.indexOf('#') + 1,
          clipPathUrl.length - 1
        )}`
      )
    : null;

/**
 * @returns boolean, indicating whether or not the element is clipped by its clipPath
 */
const isElementClipped = (
  svg: SVGElement,
  clippedElement: Element,
  clipPathUrl: string
) => {
  const clipPathElement = getClipPathElementByUrl(svg, clipPathUrl);
  return clipPathElement && clippedElement
    ? intersects(
        clipPathElement?.getBoundingClientRect(),
        clippedElement.getBoundingClientRect()
      )
    : false;
};

const getSVGStyledString = async (
  svg: SVGElement,
  strLegendWrappedInSVG: string
) => {
  Array.from(svg.querySelectorAll(NO_EXPORT_SELECTOR)).forEach(node =>
    node.remove()
  );

  const bodyElements = svg.querySelectorAll('*:not(style):not(defs)');
  for (const node of bodyElements) {
    if (node.nodeName.toLowerCase() === 'image') {
      const imageNode = node as SVGImageElement;
      const href = imageNode.href.baseVal;
      const dataUri = await ensureImageDataUri(href);
      node.setAttribute('href', dataUri);
      node.setAttribute('crossorigin', 'anonymous');
    }
    const isSvgElement = node instanceof SVGElement;
    if (isSvgElement) {
      [...node.attributes].forEach(({ name, value }) =>
        node.setAttribute(name, escapeHTMLAttribute(value))
      );
    }
    setElementStyle(node as SVGElement);

    // #region this is a hack to work around a bug in Safari where getComputedStyle(el).transform returns 'none' even when the transform attribute is set.
    if (
      isSafari() &&
      isSvgElement &&
      node.hasAttribute('transform') &&
      node.style.transform === 'none'
    ) {
      node.style.removeProperty('transform');
    }
    // #endregion
  }
  const exportSVG = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      ${svg.outerHTML}${strLegendWrappedInSVG}
    </svg>`;
  return exportSVG;
};

const getBounds = (svg: SVGElement) => {
  const d3Container = svg.querySelector(MAIN_GROUP_CONTAINER_SELECTOR);
  const svgRect = svg.getBoundingClientRect(); // use bbox for FF ?
  if (d3Container) {
    const rect = d3Container.getBoundingClientRect();
    const containerClipPath = d3Container.getAttribute('clip-path');
    if (
      containerClipPath &&
      isElementClipped(svg, d3Container, containerClipPath)
    ) {
      const visibleAreaRect = getVisibleClippedAreaBounds(svg, d3Container);
      return {
        width: visibleAreaRect?.width || rect.width,
        height: visibleAreaRect?.height || rect.height,
      };
    }

    return {
      width: rect.width,
      height: rect.height,
    };
  }
  return {
    width: svgRect.width,
    height: svgRect.height,
  };
};

const getSVGBlob = async ({
  svg,
  legendElement,
  getStyleSheets,
  name,
  mutableTrackingData,
  profilingTransaction,
}: Pick<
  GetSVGExportBlobArguments,
  | 'svg'
  | 'legendElement'
  | 'getStyleSheets'
  | 'mutableTrackingData'
  | 'profilingTransaction'
> & { name?: ViewIds }) => {
  let legendBBox;
  let strLegendWrappedInSVG = '';
  if (svg.style && svg.style.getPropertyValue('overflow') === 'hidden') {
    svg.style.setProperty('overflow', 'visible');
  }

  if (name === ViewIds.NETWORK) {
    scaleAndFitNetworkView(svg);
  }
  if (
    name === ViewIds.BLOCK_DIAGRAM ||
    name === ViewIds.MODERNIZED_BLOCK_DIAGRAM
  ) {
    // yFiles adds a clipPath as a first svg element, which is enlosing the contents with a certain
    // padding. It is not needed for the export AFAICT, since we fit the contents manually, but it
    // cuts off the translated elements, such as counters. So we remove it.
    svg.querySelector('clipPath')?.remove();
  }

  // clone the legend, scale it and pass its DOMRect to the export formatting
  if (legendElement) {
    // scale and clone the legend DOM
    legendElement.classList.add('forExport');
    const clonedLegend = await getClonedDOM(
      legendElement,
      'viewLegend',
      mutableTrackingData,
      profilingTransaction
    );
    clonedLegend.style.setProperty('position', 'static');

    // the max/min-width is scaled within the styled component css,
    // and the height is computed with fit-content
    const scaledLegendHeight = legendElement.scrollHeight;
    const scaledLegendWidth = legendElement.scrollWidth;
    clonedLegend.style.setProperty('height', `${scaledLegendHeight}px`);
    clonedLegend.style.setProperty('width', `${scaledLegendWidth}px`);

    legendElement.classList.remove('forExport');

    const { width } = getBounds(svg);

    const mainGroupContainer = svg.querySelector(MAIN_GROUP_CONTAINER_SELECTOR);
    const isSafari = document.documentElement.classList.contains('Safari');

    const legendX =
      width +
      HTML_EXPORT_LEGEND_PADDING * 2 +
      (mainGroupContainer && !isSafari ? PADDING / 2 : 0);
    const legendY = HTML_EXPORT_LEGEND_PADDING;

    legendBBox = new DOMRect(
      legendX,
      legendY,
      scaledLegendWidth,
      scaledLegendHeight
    );

    // create serializede legend svg with legendBBox dimensions
    const serializedStyleElement = await getStyles(
      clonedLegend,
      getStyleSheets,
      '',
      profilingTransaction
    );
    strLegendWrappedInSVG = serializeAndWrapInSVG(
      profilingTransaction,
      clonedLegend,
      scaledLegendWidth,
      scaledLegendHeight,
      serializedStyleElement,
      [legendBBox.x, legendBBox.y]
    );
  }

  const serializedStyleElement = await getStyles(
    svg,
    getStyleSheets,
    '',
    profilingTransaction
  );
  const clonedImageColorFilters = cloneImageColorFilters();

  const stringFormattingSpan = profiling.startSpan(
    profilingTransaction,
    'getSVGBlob - regex and string formating in serialized svg'
  );
  // wrap vis svg and strLegendWrappedInSVG value into an "envelope" svg
  const svgText = await getSVGStyledString(svg, strLegendWrappedInSVG);

  // Set the top level export svg to accomodate the legend and the vis svg's,
  const svgTextFormatted = formatSVGForExport({
    svgText,
    ...getBounds(svg),
    legendBBox,
  });
  const svgType = /^<svg\b[^>]*xmlns:\w+=/.test(svgTextFormatted)
    ? svgTextFormatted
    : svgTextFormatted.replace('<svg', `<svg ${SVG_IMPORTS}`);
  const graphVisualizationSvgDefElements = renderToStaticMarkup(
    createElement('svg', null, createElement(GraphVisualizationSvgDefElements))
  ).replace(/^<svg[^>]*>|<\/svg>$/g, '');

  const svgSpriteIcons = Array.from(
    document.querySelectorAll('[id^=svg_sprite]')
  )
    .map(element => element.outerHTML)
    .join('');

  const svgData = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<!DOCTYPE svg [<!ENTITY nbsp "&#160;">]>
${svgType}`
    .replace(
      '<svg style="(.*?)"',
      '<svg style="$1;fill:white;background:white;width:auto;height:auto" '
    )
    .replace(
      /<defs.*?>/im,
      `<defs>${serializedStyleElement}${getSvgDefs()}${graphVisualizationSvgDefElements}${clonedImageColorFilters || ''}${svgSpriteIcons}`
    )
    .replace(/(?:\s(?:NS\d+:)?(href=".+?"))+/gi, ' $1 xlink:$1'); // to support SVG specs 1.1 and 2.0, we need to include both xlink:href and href

  profiling.endSpan(stringFormattingSpan);

  return new Blob([svgData], {
    type: 'image/svg+xml',
  });
};

// create a canvas for the export with - or without legend, depending on whether legendElement is truthy
const createCanvasForExport = async ({
  svg,
  legendElement,
  getStyleSheets,
  name,
  mutableTrackingData,
  profilingTransaction,
}: Pick<
  GetSVGExportBlobArguments,
  | 'svg'
  | 'legendElement'
  | 'getStyleSheets'
  | 'mutableTrackingData'
  | 'profilingTransaction'
> & { name: ViewIds }) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise<HTMLCanvasElement>(async (resolve, reject) => {
    const svgBlob = await getSVGBlob({
      svg,
      getStyleSheets,
      name,
      mutableTrackingData,
      profilingTransaction,
    });
    const padding = PADDING / 2;
    const canvas = createExportCanvas();
    const url = window.URL.createObjectURL(svgBlob);
    const img = new Image();

    const legendCanvas = legendElement
      ? await getCanvasOfDOM(
          legendElement,
          'viewLegend',
          getStyleSheets,
          mutableTrackingData,
          profilingTransaction
        )
      : null;

    img.crossOrigin = 'anonymous';
    img.onerror = reject;
    img.onload = async () => {
      // In Safari the data uri fonts are not loaded in the image load event.
      // Obviously the delay is a bit random, but just making it async is
      // not enough.
      await new Promise(resolve => setTimeout(resolve, 100));
      const ctx = canvas.getContext('2d');
      const { width, height, scale } = getCanvasMaxBounds(
        img.width,
        img.height
      );

      if (ctx) {
        // if the largest side of img is > 2500, then scale the legend by 2.
        // 2500 is a "sweet spot" picked by visual observation for the legend to have a reasonable
        // size and be readable on the same zoom level on which the graph labels are readable
        const legendScaleFactor = width < 2500 && height < 2500 ? 1 : 2;

        ctx.scale(scale, scale);
        canvas.width =
          width +
          padding +
          (legendCanvas
            ? legendCanvas.width * scale * legendScaleFactor + padding / 2
            : 0);
        canvas.height =
          legendCanvas &&
          legendCanvas.height * scale * legendScaleFactor > height
            ? legendCanvas.height * scale * legendScaleFactor + padding
            : height + padding;

        ctx.drawImage(img, padding / 2, padding / 2, width, height);

        if (legendCanvas) {
          ctx.drawImage(
            legendCanvas,
            width,
            padding / 2,
            legendCanvas.width * scale * legendScaleFactor,
            legendCanvas.height * scale * legendScaleFactor
          );
        }
      }
      window.URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.src = url;
  });

const getPNGBlob = async ({
  svg,
  legendElement,
  headerElement,
  getStyleSheets,
  name,
  mutableTrackingData,
  profilingTransaction,
}: Pick<
  GetSVGExportBlobArguments,
  | 'svg'
  | 'legendElement'
  | 'headerElement'
  | 'getStyleSheets'
  | 'mutableTrackingData'
  | 'profilingTransaction'
> & { name: ViewIds }) =>
  canvasToBlob(
    await getCanvasWithBackgroundColor({
      profilingTransaction,
      mutableTrackingData,
      inputCanvas: await createCanvasForExport({
        profilingTransaction,
        mutableTrackingData,
        svg,
        legendElement,
        getStyleSheets,
        name,
      }),
      color: '#FFFFFF',
      getStyleSheets,
      viewHeader: headerElement,
    })
  );

type GetSVGExportBlobArguments = {
  svg: SVGElement;
  format: EXPORT_FORMAT;
  legendElement?: HTMLElement;
  headerElement?: HTMLElement;
  getStyleSheets: GetStyleSheetsFn;
  name: ViewIds;
  mutableTrackingData: ExportTrackingData;
  profilingTransaction: profiling.Transaction;
};
export const getExportBlob = async ({
  svg,
  format,
  legendElement,
  headerElement,
  getStyleSheets,
  name,
  mutableTrackingData,
  profilingTransaction,
}: GetSVGExportBlobArguments) =>
  format === EXPORT_FORMAT.SVG
    ? getSVGBlob({
        mutableTrackingData,
        profilingTransaction,
        svg,
        legendElement,
        getStyleSheets,
        name,
      })
    : getPNGBlob({
        mutableTrackingData,
        profilingTransaction,
        svg,
        legendElement,
        headerElement,
        getStyleSheets,
        name,
      });

const scaleCanvasForBlob = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
  const { width, height } = canvas;
  const area = width * height;
  const maximumDimension = Math.max(width, height);
  const exceedsPixelLimit = area > CANVAS_MAXIMUM_AREA;
  const exceedsDimensionLimit = maximumDimension > CANVAS_MAXIMUM_DIMENSION;

  if (!exceedsPixelLimit && !exceedsDimensionLimit) {
    return canvas;
  }

  const scale = Math.min(
    1,
    exceedsPixelLimit ? CANVAS_MAXIMUM_AREA / area : 1,
    exceedsDimensionLimit ? CANVAS_MAXIMUM_DIMENSION / maximumDimension : 1
  );

  const scaledCanvas = document.createElement('canvas');
  const scaledWidth = Math.floor(width * scale);
  const scaledHeight = Math.floor(height * scale);

  scaledCanvas.width = scaledWidth;
  scaledCanvas.height = scaledHeight;

  const ctx = scaledCanvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(canvas, 0, 0, scaledWidth, scaledHeight);

  return scaledCanvas;
};

const EMPTY_BLOB_MESSAGE = 'Empty blob when exporting to png';

const toEmptyBlobError = ({ width, height }: HTMLCanvasElement): Error =>
  width > 0 && height > 0
    ? toExportTooLargeError(EMPTY_BLOB_MESSAGE)
    : Error(EMPTY_BLOB_MESSAGE);

export const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    const scaledCanvas = scaleCanvasForBlob(canvas);
    scaledCanvas.toBlob((blob: Blob | null) =>
      blob ? resolve(blob) : reject(toEmptyBlobError(scaledCanvas))
    );
  });

const getCanvasMaxBounds = (width: number, height: number) => {
  // Needs to be within browser limits
  // https://stackoverflow.com/questions/6081483/maximum-size-of-a-canvas-element
  const MAX_CANVAS_SIZE = 32000;
  const scaleFactor =
    1 /
    Math.max(
      1,
      (DEVICE_PIXEL_RATIO * width) / MAX_CANVAS_SIZE,
      (DEVICE_PIXEL_RATIO * height) / MAX_CANVAS_SIZE
    );
  return {
    width: width * scaleFactor,
    height: height * scaleFactor,
    scale: scaleFactor,
  };
};
